import {BaseQueryFn, createApi} from "@reduxjs/toolkit/query/react";
import {Database, TablesUpdate} from "./apitypes";
import {PostgrestError} from "@supabase/supabase-js";
import {supabase} from "./supabase";

export type TableName = keyof (Database["public"]["Tables"])
export type TableOrViewName = keyof (Database["public"]["Tables"] & Database["public"]["Views"])
export type FunctionName = keyof (Database["public"]["Functions"])

type InsertArgs = {
    table: TableName
    method: "INSERT";
    body: Record<string, any>
}

type DelArgs = {
    table: TableName
    method: "DELETE";
    key?: string
    id: string | number;
}

type UpdateArgs = {
    table: TableName
    method: "UPDATE";
    body: Record<string, any>
    key?: string
    id: string | number;
}

type GetArgs = {
    table: TableOrViewName
    method: "GET";
    select?: string
    single?: boolean
    key?: string
    id?: string | number;
}

type RpcArgs = {
    rpc: FunctionName;
    params: Record<string, any>;
}

export type SupabaseQueryArgs = RpcArgs | GetArgs | UpdateArgs | DelArgs | InsertArgs


const supabaseBaseQuery: BaseQueryFn<SupabaseQueryArgs, unknown, PostgrestError> = async (args: SupabaseQueryArgs) => {


    if ("rpc" in args) {
        //
        // @ts-ignore
        const {data, error} = await supabase.rpc(args.rpc, args.params);
        if (error) throw error;
        return {data}
    }

    if (!args.table) {
        throw new Error(`Missing table ${JSON.stringify(args)}`)
    }
    if (["GET", "UPDATE", "DELETE"].indexOf(args.method) >= 0) {
        if (!("id" in args)) {
            throw new Error(`Id missing ${args.method}`)
        }
    }

    // @ts-ignore
    let query = supabase.from(args.table);
    switch (args.method) {
        case 'GET':
            //@ts-ignore
            query = query.select(args.select || "*").eq(args.key || "id", args.id);
            if (args.single) {
                //@ts-ignore
                query = query.maybeSingle()
            }
            break;
        case 'INSERT':
            // @ts-ignore
            query = query.insert(args.body).select().single();
            break;
        case 'UPDATE':
            // @ts-ignore
            query = query.update(args.body).eq(args.key || "id", args.id);
            break;
        case 'DELETE':
            // @ts-ignore
            query = query.delete().eq(args.key || "id", args.id);
            break;
        default:
            throw new Error(`Unsupported method: ${JSON.stringify(args)}`);
    }

    // @ts-ignore
    const {data, error} = await query;
    if (error) return {error};
    return {data};
};


export const insertFn = (table: TableName) => (body: Record<string, any>): InsertArgs => ({
    body,
    table,
    method: "INSERT"
})

export const updateFn = (table: TableName) => (a: {
    id: string | number,
    body: Record<string, any>,
    key?: string
}): UpdateArgs => ({
    ...a,
    table,
    method: "UPDATE"
})
export const deleteFn = (table: TableName) => (a: { id: string | number, key?: string }): DelArgs => ({
    ...a,
    table,
    method: "DELETE"
})
export const getFn = (table: TableOrViewName) => (a: {
    id: string | number,
    key?: string,
    select?: string,
    single?: boolean
}): GetArgs => ({
    single: true,
    ...a,
    table,
    method: "GET"
})

export const api = createApi({
    baseQuery: supabaseBaseQuery,
    tagTypes: [
        "Person",
        "Machine",
        "Operation",
        "Registration",
        "WorkOrder",
        "Partner",
        "Product",
        "Interrupt",
        "Profile",
        "WorkOrderOrderedProduct",
        "RegImage"
    ],
    endpoints: () => ({}),
});


export type GetApiResponse<T> = /** status 200 Items from Model */ {
    count?: number;
    result?: T;
};


export type PostApiResponse = /** status 201 Item inserted */ {
    id: string
    success: boolean
};


export interface GetParams {
    page?: number
    pageSize?: number
    filter?: FilterColumn[]
    sort?: SortColumn[]
    groupBy?: string
}

export type GetRisonApiArg = {
    q?: GetParams
};


export interface IdResult {
    id: number;
}

export type IdApiArg = {
    id: number
    key?: "id"
    select?: string
} | {
    id: number | string
    key?: string
    select?: string
};


export interface UpdateResp {
    status: "ok",
    id?: number
}

export type UUIDApiArg = {
    id: string
};

export interface UpdateArg<T extends keyof Database["public"]["Tables"]> {
    body: TablesUpdate<T>;
    id: number;
}

export interface UpdateRegRef<T extends keyof Database["public"]["Tables"]> {
    body: TablesUpdate<T>;
    id: number;
    reg: number;
}


export const prepareFTX = (q: string) => {

    if (q.match(/[&|:*()]/)) {
        return q;
    }

    return q
        .split(/\s+/)
        .filter(t => t.trim().length > 0)
        .map(token => {
            if (token.match(/[\w\d]+/)) {
                return token + ":*"
            }
            return token
        })
        .join("&")
}

// vali filter values
type OperatorValue =
    "startWith"
    | "notStartWith"
    | "endWith"
    | "notEndWith"
    | "ct"
    | "nct"
    | "eq"
    | "ne"
    | "gt"
    | "lt"
    | "isEmpty"
    | "isNotEmpty"
    | "gte"
    | "lte"
    | "in"
    | "nin"
    | "all"
    | "$text"
    | "$or"
    | "$and";

export type FilterValue = string | number | boolean | Array<string> | Array<number> | null | undefined;
export type FilterColumn = {
    col: string;
    opr: OperatorValue;
    sub?: Array<FilterColumn>;
    value?: FilterValue;
}

export type SortColumn = {
    col: string;
    dir: "asc" | "desc";

}


/**
 * Grid <-> work filter map
 */
const filterMap: Record<string, OperatorValue> = {
    contains: "ct",
    startsWith: "startWith",
    endsWith: "endWith",
    notEndWith: "notEndWith",
    nsw: "notStartWith",
    equals: "eq",
    isAnyOf: "in",
    is: "eq",
    "=": "eq",
    "!=": "ne",
    not: "ne",
    ">": "gt",
    ">=": "gte",
    "<=": "lte",
    "<": "lt",
    "onOrAfter": "gte",
    "after": "gt",
    "before": "lt",
    "onOrBefore": "lte",
    isEmpty: "isEmpty",
    isNotEmpty: "isNotEmpty",
}

export interface PaginatedResult<T> {
    count: number | null;
    result: T[];
}

export default api;

